import { createSlice, PayloadAction } from '@reduxjs/toolkit'

// Toilet facility sub-filters
export interface ToiletFacilityFilters {
  showAccessible: boolean
  showAccessibleParking: boolean
  showNonAccessible: boolean
}

// Public event type sub-filters
export interface PublicEventTypeFilters {
  showWadsa: boolean
  showParkrun: boolean
  showCommunity: boolean
}

// INIT STATE
export interface MapFiltersState {
  showPublicEvents: boolean
  publicEventTypeFilters: PublicEventTypeFilters
  showToiletFacilities: boolean
  toiletFacilityFilters: ToiletFacilityFilters
  showUserEvents: boolean
  showCustomMarkers: boolean
  showPastEvents: boolean
  showConnections: boolean
  showRecurringEvents: boolean
  searchQuery: string
  isFilterPanelOpen: boolean
  initializedPreset: 'public' | 'private' | 'organization' | null
}

const initialToiletFilters: ToiletFacilityFilters = {
  showAccessible: false,
  showAccessibleParking: false,
  showNonAccessible: false,
}

const initialPublicEventTypeFilters: PublicEventTypeFilters = {
  showWadsa: true,
  showParkrun: true,
  showCommunity: true,
}

const initialState: MapFiltersState = {
  showPublicEvents: true,
  publicEventTypeFilters: initialPublicEventTypeFilters,
  showToiletFacilities: false,
  toiletFacilityFilters: initialToiletFilters,
  showUserEvents: true,
  showCustomMarkers: true,
  showPastEvents: false,
  showConnections: true,
  showRecurringEvents: true,
  searchQuery: '',
  isFilterPanelOpen: false,
  initializedPreset: null,
}

// Type for boolean filter keys (excluding object types like toiletFacilityFilters and publicEventTypeFilters)
type BooleanFilterKey = keyof Omit<
  MapFiltersState,
  | 'searchQuery'
  | 'isFilterPanelOpen'
  | 'initializedPreset'
  | 'toiletFacilityFilters'
  | 'publicEventTypeFilters'
>

// SLICE
const mapFiltersSlice = createSlice({
  name: 'mapFilters',
  initialState,
  reducers: {
    toggleFilter: (state, action: PayloadAction<BooleanFilterKey>) => {
      const filterKey = action.payload
      state[filterKey] = !state[filterKey]
    },
    toggleToiletFilter: (state, action: PayloadAction<keyof ToiletFacilityFilters>) => {
      const filterKey = action.payload
      state.toiletFacilityFilters[filterKey] = !state.toiletFacilityFilters[filterKey]
    },
    setToiletFilters: (state, action: PayloadAction<Partial<ToiletFacilityFilters>>) => {
      state.toiletFacilityFilters = {
        ...state.toiletFacilityFilters,
        ...action.payload,
      }
    },
    setPublicEventTypeFilters: (state, action: PayloadAction<Partial<PublicEventTypeFilters>>) => {
      state.publicEventTypeFilters = {
        ...state.publicEventTypeFilters,
        ...action.payload,
      }
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload.toLowerCase()
    },
    toggleFilterPanel: (state) => {
      state.isFilterPanelOpen = !state.isFilterPanelOpen
    },
    setFilterPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.isFilterPanelOpen = action.payload
    },
    setEventFilters: (
      state,
      action: PayloadAction<{
        showPublicEvents?: boolean
        showUserEvents?: boolean
        showRecurringEvents?: boolean
        showPastEvents?: boolean
      }>
    ) => {
      if (typeof action.payload.showPublicEvents === 'boolean') {
        state.showPublicEvents = action.payload.showPublicEvents
      }
      if (typeof action.payload.showUserEvents === 'boolean') {
        state.showUserEvents = action.payload.showUserEvents
      }
      if (typeof action.payload.showRecurringEvents === 'boolean') {
        state.showRecurringEvents = action.payload.showRecurringEvents
      }
      if (typeof action.payload.showPastEvents === 'boolean') {
        state.showPastEvents = action.payload.showPastEvents
      }
    },
    resetEventFilters: (state) => {
      state.showPublicEvents = true
      state.showUserEvents = true
      state.showRecurringEvents = true
    },
    resetFilters: (state) => {
      return {
        ...initialState,
        isFilterPanelOpen: state.isFilterPanelOpen,
        initializedPreset: state.initializedPreset,
      }
    },
    setPublicMapDefaults: (state) => {
      state.showPublicEvents = true
      state.publicEventTypeFilters = initialPublicEventTypeFilters
      state.showToiletFacilities = false
      state.toiletFacilityFilters = initialToiletFilters
      state.showUserEvents = false
      state.showCustomMarkers = false
      state.showPastEvents = false
      state.showConnections = false
      state.initializedPreset = 'public'
    },
    setPrivateMapDefaults: (state) => {
      state.showPublicEvents = true
      state.publicEventTypeFilters = initialPublicEventTypeFilters
      state.showToiletFacilities = false
      state.toiletFacilityFilters = initialToiletFilters
      state.showUserEvents = true
      state.showCustomMarkers = true
      state.showPastEvents = false
      state.showConnections = true
      state.initializedPreset = 'private'
    },
    setOrganizationMapDefaults: (state) => {
      state.showPublicEvents = true
      state.publicEventTypeFilters = initialPublicEventTypeFilters
      state.showToiletFacilities = false
      state.toiletFacilityFilters = initialToiletFilters
      state.showUserEvents = false
      state.showCustomMarkers = false
      state.showPastEvents = false
      state.showConnections = false
      state.initializedPreset = 'organization'
    },
  },
})

// Export actions
export const {
  toggleFilter,
  toggleToiletFilter,
  setToiletFilters,
  setPublicEventTypeFilters,
  setSearchQuery,
  toggleFilterPanel,
  setFilterPanelOpen,
  setEventFilters,
  resetEventFilters,
  resetFilters,
  setPublicMapDefaults,
  setPrivateMapDefaults,
  setOrganizationMapDefaults,
} = mapFiltersSlice.actions

// Export reducer
export default mapFiltersSlice.reducer
